import { ActionIcon, Avatar, Box, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash, IconUpload } from '@tabler/icons-react';
import { useRef, useState } from 'react';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { t } from '../dictionary';

const IMAGE_TYPE_AVATAR = 'user_avatars';

type Props = {
    value: string | null;
    name: string;
    onChange: (url: string | null) => void;
    disabled?: boolean;
};

export const AvatarUpload = ({ value, name, onChange, disabled }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const pickFile = () => {
        if (!disabled && !uploading) inputRef.current?.click();
    };

    const handleFile = async (file: File | undefined) => {
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            notifications.show({ color: 'red', message: t.settings.photoInvalid });

            return;
        }

        setUploading(true);

        try {
            const { data } = await api.uploadImage(file, IMAGE_TYPE_AVATAR);

            onChange(data.url);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Stack gap='xs'>
            <Text size='sm' fw={500}>
                {t.settings.photo}
            </Text>
            <Text size='xs' c='dimmed'>
                {t.settings.photoHint}
            </Text>

            <Group gap='md' align='center'>
                <Box style={{ position: 'relative' }}>
                    <Avatar
                        src={value || undefined}
                        color='indigo'
                        radius='xl'
                        size={72}
                        style={{ cursor: disabled || uploading ? 'default' : 'pointer' }}
                        onClick={pickFile}
                    >
                        {name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    {uploading && (
                        <Box
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.65)',
                            }}
                        >
                            <Loader size={18} />
                        </Box>
                    )}
                </Box>

                <Group gap='xs'>
                    <Tooltip label={t.settings.changePhoto}>
                        <ActionIcon
                            variant='light'
                            color='indigo'
                            radius='xl'
                            onClick={pickFile}
                            disabled={disabled || uploading}
                            aria-label={t.settings.changePhoto}
                        >
                            <IconUpload size={16} />
                        </ActionIcon>
                    </Tooltip>
                    {value && (
                        <Tooltip label={t.settings.removePhoto}>
                            <ActionIcon
                                variant='light'
                                color='red'
                                radius='xl'
                                onClick={() => onChange(null)}
                                disabled={disabled || uploading}
                                aria-label={t.settings.removePhoto}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            </Group>

            <input
                ref={inputRef}
                type='file'
                accept='image/*'
                hidden
                onChange={(e) => {
                    void handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
        </Stack>
    );
};
